import { Link } from "@tanstack/react-router";
import { motion, useInView } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import CampaignCta from "@/components/CampaignCta";
import { isCampaignVisit } from "@/lib/attribution";
import { CheckCircle2, MapPin, ArrowRight, Star, CalendarCheck, FileText, ShieldCheck } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FloatingButtons from "@/components/FloatingButtons";

export type RelatedLink = {
  to: string;
  title: string;
  desc: string;
  /** Optional photo shown on top of the card. */
  image?: string;
  /** Show the whole photo uncropped (portrait photos of people). */
  imageWhole?: boolean;
};


import PricingTerms from "@/components/PricingTerms";
import CorporateAccountForm from "@/components/CorporateAccountForm";
import ContactButtons from "@/components/ContactButtons";
import TextMariaSection from "@/components/TextMariaSection";
import FamilyChauffeurs from "@/components/FamilyChauffeurs";
import BrandLine from "@/components/BrandLine";

/** `group` (optional) shows the question under a heading such as "Safety & Licensing". */
export type FaqItem = { q: string; a: string; group?: string };

export type SeoLandingProps = {
  eyebrow: string;
  h1: string;
  intro: string;
  showPricingTerms?: boolean;
  showCorporateForm?: boolean;
  /** Hide the pricing line above the quote form (pages that show no prices). */
  hideQuotePricing?: boolean;
  heroImage: string;
  heroAlt: string;
  /** CSS object-position for the hero photo (e.g. "50% 60%") so portrait photos frame the subject. */
  heroPosition?: string;
  heroVideo?: string;
  midImage: string;
  midAlt: string;
  // Section 1: who/what
  sectionOneTitle: string;
  sectionOneBody: string[];
  // Bullets next to mid image
  bulletsTitle: string;
  bulletsIntro: string;
  bullets: string[];
  // Coverage
  coverageTitle: string;
  coverageIntro: string;
  coverage: string[];
  /** Replaces the "Coverage" label (and uses check icons instead of map pins in the rich layout). */
  coverageEyebrow?: string;
  // Optional route table with real drive detail
  routeTable?: {
    title: string;
    intro: string;
    rows: { route: string; distance: string; time: string; notes: string }[];
    footnote?: string;
  };
  // Optional comparison table (two columns)
  compareTable?: {
    title: string;
    intro?: string;
    headers: [string, string];
    rows: [string, string][];
  };
  // Optional long-form detail sections (terminal/FBO instructions, etc.)
  /** `imageWhole` shows the photo uncropped (portrait photos of people). */
  detailSections?: { title: string; body?: string[]; list?: string[]; image?: string; imageAlt?: string; imageWhole?: boolean }[];
  /**
   * Fuller layout: photo beside the intro, quick-fact cards, booking bands between
   * sections, "how booking works" steps, featured gallery and a split FAQ with a booking card.
   */
  rich?: boolean;
  quickFacts?: { value: string; label: string }[];
  introImage?: string;
  introImageAlt?: string;
  /** Text for the two mid-page booking bands (after the features, after the gallery). */
  bookingBands?: { title: string; body: string }[];
  /** Photo on the booking card beside the FAQ (rich layout). */
  faqImage?: string;
  faqImageAlt?: string;
  // Optional fleet photo gallery
  gallery?: {
    title: string;
    intro?: string;
    images: { src: string; alt: string; caption?: string }[];
    /** Show photos in a tall frame (for portrait photos). */
    tall?: boolean;
  };
  // Optional real client reviews (copied from Google reviews), with optional client photos
  reviews?: {
    title: string;
    intro?: string;
    photos?: { src: string; alt: string }[];
    photoCredit?: string;
    items: { name: string; date: string; text: string }[];
  };
  // FAQ
  faqs: FaqItem[];
  faqTitle?: string;
  relatedTitle?: string;
  // Related internal links
  related: RelatedLink[];
  // Final CTA
  ctaTitle: string;
  ctaBody: string;
  /** Default SMS ref code for campaign visits (a ?ref= in the URL wins). */
  refCode?: string;
};

function AnimatedSection({
  children,
  className = "",
  id,
}: {
  children: React.ReactNode;
  className?: string;
  id?: string;
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  return (
    <motion.section
      ref={ref}
      id={id}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7 }}
      className={className}
    >
      {children}
    </motion.section>
  );
}

const LG_SPAN: Record<number, string> = { 1: "lg:col-span-1", 2: "lg:col-span-2", 3: "lg:col-span-3" };

/**
 * Featured photo grid: the first photo fills a 2x2 block, and the last photo
 * stretches so no row is left with a gap (2 columns on phones, 3 on desktop).
 */
function galleryCellClass(i: number, n: number): string {
  if (n < 3) return "";
  if (i === 0) return "col-span-2 row-span-2";
  if (i !== n - 1) return "";
  const cells = 4 + (n - 1);
  const classes: string[] = [];
  if (cells % 2) classes.push("col-span-2");
  const r = cells % 3;
  if (r) classes.push(LG_SPAN[4 - r]);
  else if (cells % 2) classes.push("lg:col-span-1");
  return classes.join(" ");
}

/** Shows the entire photo (no cropping) over a soft blurred copy of itself, for portrait photos of people. */
function WholePhoto({ src, alt, className = "" }: { src: string; alt: string; className?: string }) {
  return (
    <div className={`relative w-full overflow-hidden bg-muted ${className}`}>
      <img src={src} alt="" aria-hidden="true" loading="lazy" className="absolute inset-0 w-full h-full object-cover scale-110 blur-xl opacity-60" />
      <img src={src} alt={alt} loading="lazy" className="relative w-full h-full object-contain" />
    </div>
  );
}

/** FAQ cards; when items carry a `group`, they render under group headings in first-seen order. */
function FaqList({ faqs, spacing }: { faqs: FaqItem[]; spacing: string }) {
  const card = (f: FaqItem, Heading: "h3" | "h4") => (
    <div key={f.q} className="luxury-card rounded-lg p-6">
      <Heading className="font-heading text-lg font-semibold text-foreground mb-2">{f.q}</Heading>
      <p className="text-muted-foreground leading-relaxed">{f.a}</p>
    </div>
  );
  if (!faqs.some((f) => f.group)) return <div className={spacing}>{faqs.map((f) => card(f, "h3"))}</div>;
  const groups: { name: string; items: FaqItem[] }[] = [];
  for (const f of faqs) {
    const name = f.group ?? "More Questions";
    const g = groups.find((x) => x.name === name);
    if (g) g.items.push(f);
    else groups.push({ name, items: [f] });
  }
  return (
    <div className="space-y-10">
      {groups.map((g) => (
        <div key={g.name}>
          <h3 className="flex items-center gap-3 text-primary text-sm tracking-[0.25em] uppercase font-semibold mb-4">
            <span className="h-px w-8 bg-primary" aria-hidden="true" />
            {g.name}
          </h3>
          <div className={spacing}>{g.items.map((f) => card(f, "h4"))}</div>
        </div>
      ))}
    </div>
  );
}

const DEFAULT_BANDS = [
  { title: "Text Me Your Date", body: "Send me the date, pickup and group size. I'll check it myself and send your quote in writing." },
  { title: "Like What You See?", body: "Text me and I'll tell you if it's open for your date. Every quote is custom and in writing." },
];

/** Mid-page booking prompt: Text Maria, WhatsApp or call. */
function BookingBand({ title, body, location }: { title: string; body: string; location: string }) {
  return (
    <section
      data-track-location={location}
      className="py-12 sm:py-14 bg-gradient-to-r from-primary/10 via-background to-purple-light/10 border-y border-primary/15"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col lg:flex-row items-center justify-between gap-6 lg:gap-10">
        <div className="text-center lg:text-left max-w-2xl">
          <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-foreground mb-2">{title}</h2>
          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">{body}</p>
        </div>
        <ContactButtons showCall={false} className="flex-shrink-0 w-full sm:w-auto" />
      </div>
    </section>
  );
}

export default function SeoLandingPage(props: SeoLandingProps) {
  const bands = props.bookingBands ?? DEFAULT_BANDS;
  // Campaign CTA only for tagged ad/social visits; organic visitors see the page unchanged.
  const [campaign, setCampaign] = useState(false);
  useEffect(() => setCampaign(isCampaignVisit()), []);
  return (
    <>
      <Header />
      <main>
        {/* Hero */}
        <section className="relative min-h-[640px] sm:min-h-[80vh] flex items-center justify-center overflow-hidden pt-24 pb-10 sm:pb-12">
          <div className="absolute inset-0">
            {props.heroVideo ? (
              <video
                src={props.heroVideo}
                poster={props.heroImage}
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                className="w-full h-full object-cover"
                aria-label={props.heroAlt}
              />
            ) : (
              <img
                src={props.heroImage}
                alt={props.heroAlt}
                width={1920}
                height={1080}
                className="w-full h-full object-cover"
                style={props.heroPosition ? { objectPosition: props.heroPosition } : undefined}
              />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/60 to-black/40" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-transparent to-black/40" />
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 w-full text-center">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: "easeOut" }}
              className="max-w-4xl mx-auto"
            >
              <p className="text-purple-light text-xs font-medium tracking-[0.4em] uppercase mb-6">
                {props.eyebrow}
              </p>

              <h1 className="font-heading text-[2rem] leading-[1.1] sm:text-5xl lg:text-6xl xl:text-7xl font-semibold sm:leading-[1.05] mb-6 sm:mb-8 text-white">
                <span className="hero-gradient-text">{props.h1}</span>
              </h1>

              <p className="text-white/80 text-base sm:text-xl leading-relaxed mb-8 sm:mb-10 max-w-3xl mx-auto">
                {props.intro}
              </p>

              {props.showPricingTerms && (
                <PricingTerms className="text-purple-light max-w-3xl mx-auto mb-8" />
              )}

              {campaign ? (
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <CampaignCta refCode={props.refCode} />
                </div>
              ) : (
                <ContactButtons variant="hero" />
              )}
              <BrandLine />
            </motion.div>
          </div>
        </section>

        {/* Quick facts */}
        {props.quickFacts && props.quickFacts.length > 0 && (
          <section className="bg-background pt-10 sm:pt-12">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <ul className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                {props.quickFacts.map((f) => (
                  <li key={f.label} className="luxury-card rounded-lg px-4 py-5 sm:py-6 text-center">
                    <p className="font-heading text-xl sm:text-2xl font-semibold text-primary mb-1">{f.value}</p>
                    <p className="text-muted-foreground text-xs sm:text-sm leading-snug">{f.label}</p>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        )}

        {/* Intro / context */}
        <AnimatedSection className="section-padding bg-background">
          {props.introImage ? (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
              <div>
                <div className="accent-line-left mb-6" />
                <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-foreground mb-8">
                  {props.sectionOneTitle}
                </h2>
                <div className="space-y-5">
                  {props.sectionOneBody.map((p, i) => (
                    <p key={i} className="text-muted-foreground text-lg leading-relaxed">
                      {p}
                    </p>
                  ))}
                </div>
                <ContactButtons showCall={false} className="mt-8 sm:justify-start" />
              </div>
              <img
                src={props.introImage}
                alt={props.introImageAlt ?? ""}
                width={1200}
                height={900}
                loading="lazy"
                className="w-full h-[22rem] sm:h-[30rem] object-cover rounded-lg shadow-2xl"
              />
            </div>
          ) : (
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="accent-line-center mb-6" />
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-foreground mb-8 text-center">
                {props.sectionOneTitle}
              </h2>
              <div className="space-y-6">
                {props.sectionOneBody.map((p, i) => (
                  <p key={i} className="text-muted-foreground text-lg leading-relaxed">
                    {p}
                  </p>
                ))}
              </div>
            </div>
          )}
        </AnimatedSection>

        {/* Bullets + image */}
        <AnimatedSection className="section-padding bg-surface">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <img
                  src={props.midImage}
                  alt={props.midAlt}
                  width={1280}
                  height={832}
                  loading="lazy"
                  className="w-full h-auto rounded-lg shadow-2xl"
                />
              </div>
              <div>
                <div className="accent-line-left mb-6" />
                <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-foreground mb-6">
                  {props.bulletsTitle}
                </h2>
                <p className="text-muted-foreground text-lg leading-relaxed mb-8">
                  {props.bulletsIntro}
                </p>
                <ul className="space-y-3">
                  {props.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                      <span className="text-foreground">{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </AnimatedSection>

        {props.rich && bands[0] && <BookingBand title={bands[0].title} body={bands[0].body} location="features_cta" />}

        {/* Coverage */}
        <AnimatedSection className="section-padding bg-background">
          <div className={`${props.rich ? "max-w-6xl" : "max-w-5xl"} mx-auto px-4 sm:px-6 lg:px-8`}>
            <div className="text-center mb-12">
              <p className="text-primary text-sm tracking-[0.3em] uppercase mb-4 font-medium">
                {props.coverageEyebrow ?? "Coverage"}
              </p>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-foreground mb-6">
                {props.coverageTitle}
              </h2>
              <p className="text-muted-foreground text-lg leading-relaxed max-w-3xl mx-auto">
                {props.coverageIntro}
              </p>
            </div>
            {props.rich ? (
              <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {props.coverage.map((c) => (
                  <li key={c} className="luxury-card rounded-lg px-5 py-4 flex items-center gap-3">
                    {props.coverageEyebrow ? (
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0" />
                    ) : (
                      <MapPin className="w-5 h-5 text-primary flex-shrink-0" />
                    )}
                    <span className="text-foreground font-medium">{c}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 max-w-3xl mx-auto">
                {props.coverage.map((c) => (
                  <li key={c} className="flex items-start gap-2">
                    <MapPin className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-foreground">{c}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </AnimatedSection>

        {/* Route table */}
        {props.routeTable && (
          <AnimatedSection className="section-padding bg-surface">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-10">
                <p className="text-primary text-sm tracking-[0.3em] uppercase mb-4 font-medium">
                  Routes &amp; Drive Times
                </p>
                <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-foreground mb-6">
                  {props.routeTable.title}
                </h2>
                <p className="text-muted-foreground text-lg leading-relaxed max-w-3xl mx-auto">
                  {props.routeTable.intro}
                </p>
              </div>
              <div className="luxury-card rounded-lg overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="px-4 py-3 font-heading text-foreground font-semibold">Route</th>
                      <th className="px-4 py-3 font-heading text-foreground font-semibold">Distance</th>
                      <th className="px-4 py-3 font-heading text-foreground font-semibold">Typical Drive Time</th>
                      <th className="px-4 py-3 font-heading text-foreground font-semibold">Route Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {props.routeTable.rows.map((r) => (
                      <tr key={r.route} className="border-b border-border/60 last:border-0">
                        <td className="px-4 py-3 text-foreground font-medium">{r.route}</td>
                        <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{r.distance}</td>
                        <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{r.time}</td>
                        <td className="px-4 py-3 text-muted-foreground">{r.notes}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {props.routeTable.footnote && (
                <p className="text-muted-foreground text-sm mt-4 text-center">
                  {props.routeTable.footnote}
                </p>
              )}
            </div>
          </AnimatedSection>
        )}

        {/* Comparison table */}
        {props.compareTable && (
          <AnimatedSection className="section-padding bg-surface">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-10">
                <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-foreground mb-6">
                  {props.compareTable.title}
                </h2>
                {props.compareTable.intro && (
                  <p className="text-muted-foreground text-lg leading-relaxed max-w-3xl mx-auto">
                    {props.compareTable.intro}
                  </p>
                )}
              </div>
              <div className="luxury-card rounded-lg overflow-x-auto">
                <table className="w-full text-left text-sm sm:text-base">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="px-4 py-3 font-heading text-foreground font-semibold">{props.compareTable.headers[0]}</th>
                      <th className="px-4 py-3 font-heading text-foreground font-semibold">{props.compareTable.headers[1]}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {props.compareTable.rows.map(([a, b]) => (
                      <tr key={a} className="border-b border-border/60 last:border-0 align-top">
                        <td className="px-4 py-3 text-foreground font-medium">{a}</td>
                        <td className="px-4 py-3 text-muted-foreground">{b}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </AnimatedSection>
        )}

        {/* Detail sections */}
        {props.detailSections && props.detailSections.length > 0 && (
          <AnimatedSection
            className={`section-padding bg-background ${
              props.rich && !props.routeTable && !props.compareTable ? "pt-0" : ""
            }`}
          >
            <div className={`${props.rich ? "max-w-6xl space-y-16 lg:space-y-20" : "max-w-4xl space-y-12"} mx-auto px-4 sm:px-6 lg:px-8`}>
              {props.detailSections.map((s, idx) => {
                const text = (
                  <div>
                    <div className="accent-line-left mb-5" />
                    <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-foreground mb-5">
                      {s.title}
                    </h2>
                    {s.body?.map((p, i) => (
                      <p key={i} className="text-muted-foreground text-lg leading-relaxed mb-4">
                        {p}
                      </p>
                    ))}
                    {s.list && (
                      <ul className="space-y-3 mt-4">
                        {s.list.map((l) => (
                          <li key={l} className="flex items-start gap-3">
                            <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                            <span className="text-foreground">{l}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
                if (props.rich && s.image) {
                  return (
                    <div key={s.title} className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                      <img
                        src={s.image}
                        alt={s.imageAlt ?? ""}
                        width={1000}
                        height={800}
                        loading="lazy"
                        className={`w-full ${s.imageWhole ? "h-auto max-w-md mx-auto" : "h-[20rem] sm:h-[26rem] object-cover"} rounded-lg shadow-2xl ${idx % 2 ? "lg:order-2" : ""}`}
                      />
                      {text}
                    </div>
                  );
                }
                if (props.rich) {
                  return (
                    <div key={s.title} className="luxury-card rounded-lg p-6 sm:p-10 grid lg:grid-cols-[1fr_auto] gap-8 items-center">
                      {text}
                      <a href="#booking" className="btn-primary rounded-sm inline-flex items-center justify-center gap-2">
                        Check My Date <ArrowRight className="w-4 h-4" />
                      </a>
                    </div>
                  );
                }
                return <div key={s.title}>{text}</div>;
              })}
            </div>
          </AnimatedSection>
        )}

        {/* How booking works */}
        {props.rich && (
          <section data-track-location="booking_steps" className="section-padding bg-surface">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <p className="text-primary text-sm tracking-[0.3em] uppercase mb-4 font-medium">How Booking Works</p>
                <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-foreground">
                  Three Steps, One Text Away
                </h2>
              </div>
              <ol className="grid md:grid-cols-3 gap-6 mb-10">
                {[
                  { icon: CalendarCheck, title: "1. Text me", body: "Your date, pickup, stops and group size, by text, WhatsApp or a call. You'll be talking to me, a real person." },
                  { icon: FileText, title: "2. Get your quote in writing", body: "I'll recommend the right vehicle and send a custom quote in writing before you book." },
                  { icon: ShieldCheck, title: "3. Secure your date", body: "A 50% deposit secures your date. Your chauffeur and vehicle are ours; we own every vehicle in our fleet." },
                ].map((step) => (
                  <li key={step.title} className="luxury-card rounded-lg p-6 sm:p-8">
                    <step.icon className="w-8 h-8 text-primary mb-4" aria-hidden="true" />
                    <h3 className="font-heading text-xl font-semibold text-foreground mb-2">{step.title}</h3>
                    <p className="text-muted-foreground leading-relaxed">{step.body}</p>
                  </li>
                ))}
              </ol>
              <ContactButtons showCall={false} />
            </div>
          </section>
        )}

        {/* Fleet gallery */}
        {props.gallery && props.gallery.images.length > 0 && (
          <AnimatedSection className="section-padding bg-background">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-10">
                <p className="text-primary text-sm tracking-[0.3em] uppercase mb-4 font-medium">
                  The Fleet
                </p>
                <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-foreground mb-6">
                  {props.gallery.title}
                </h2>
                {props.gallery.intro && (
                  <p className="text-muted-foreground text-lg leading-relaxed max-w-3xl mx-auto">
                    {props.gallery.intro}
                  </p>
                )}
              </div>
              {props.rich ? (
                <div
                  className={`grid gap-3 sm:gap-4 ${props.gallery.images.length >= 3 ? "grid-cols-2 lg:grid-cols-3" : "sm:grid-cols-2"} ${
                    props.gallery.tall || props.gallery.images.length < 3
                      ? "auto-rows-[18rem] sm:auto-rows-[22rem]"
                      : "auto-rows-[11rem] sm:auto-rows-[15rem]"
                  }`}
                >
                  {props.gallery.images.map((img, i, all) => (
                    <figure
                      key={img.src + i}
                      className={`relative rounded-lg overflow-hidden group ${galleryCellClass(i, all.length)}`}
                    >
                      <img
                        src={img.src}
                        alt={img.alt}
                        width={1000}
                        height={800}
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                      {img.caption && (
                        <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 to-transparent px-4 pt-10 pb-3 text-sm text-white">
                          {img.caption}
                        </figcaption>
                      )}
                    </figure>
                  ))}
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {props.gallery.images.map((img) => (
                    <figure key={img.src} className="luxury-card rounded-lg overflow-hidden">
                      <img
                        src={img.src}
                        alt={img.alt}
                        width={800}
                        height={600}
                        loading="lazy"
                        className={`w-full object-cover ${props.gallery?.tall ? "h-[28rem]" : "h-56"}`}
                      />
                      {img.caption && (
                        <figcaption className="px-4 py-3 text-sm text-muted-foreground">
                          {img.caption}
                        </figcaption>
                      )}
                    </figure>
                  ))}
                </div>
              )}
            </div>
          </AnimatedSection>
        )}
        {props.rich && bands[1] && <BookingBand title={bands[1].title} body={bands[1].body} location="gallery_cta" />}

        {/* Client reviews */}
        {props.reviews && props.reviews.items.length > 0 && (
          <AnimatedSection className="section-padding bg-surface">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-10">
                <p className="text-primary text-sm tracking-[0.3em] uppercase mb-4 font-medium">
                  Google Reviews
                </p>
                <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-foreground mb-6">
                  {props.reviews.title}
                </h2>
                {props.reviews.intro && (
                  <p className="text-muted-foreground text-lg leading-relaxed max-w-3xl mx-auto">
                    {props.reviews.intro}
                  </p>
                )}
              </div>
              {props.reviews.photos && props.reviews.photos.length > 0 && (
                <figure className="mb-10">
                  <div className={`grid gap-4 ${props.reviews.photos.length === 1 ? "max-w-sm mx-auto" : props.reviews.photos.length === 2 ? "sm:grid-cols-2 max-w-4xl mx-auto" : "sm:grid-cols-3"}`}>
                    {props.reviews.photos.map((p) => (
                      <img
                        key={p.src}
                        src={p.src}
                        alt={p.alt}
                        width={600}
                        height={800}
                        loading="lazy"
                        className="w-full h-auto rounded-lg"
                      />
                    ))}
                  </div>
                  {props.reviews.photoCredit && (
                    <figcaption className="text-center text-sm text-muted-foreground mt-3">
                      {props.reviews.photoCredit}
                    </figcaption>
                  )}
                </figure>
              )}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {props.reviews.items.map((r) => (
                  <blockquote key={r.name + r.date} className="luxury-card rounded-lg p-6 flex flex-col">
                    <div className="flex gap-1 mb-4" aria-label="5 out of 5 stars">
                      {[0, 1, 2, 3, 4].map((j) => (
                        <Star key={j} className="w-4 h-4 text-primary fill-primary" />
                      ))}
                    </div>
                    <p className="text-muted-foreground leading-relaxed italic mb-5 flex-1">"{r.text}"</p>
                    <footer>
                      <p className="text-foreground font-semibold">{r.name}</p>
                      <p className="text-muted-foreground text-sm">Google review, {r.date}</p>
                    </footer>
                  </blockquote>
                ))}
              </div>
            </div>
          </AnimatedSection>
        )}

        <FamilyChauffeurs compact />

        {/* FAQ */}
        <AnimatedSection className="section-padding bg-surface">
          {props.rich ? (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] gap-10 lg:gap-14 items-start">
              <aside data-track-location="faq_cta" className="lg:sticky lg:top-28">
                <p className="text-primary text-sm tracking-[0.3em] uppercase mb-4 font-medium">
                  Frequently Asked
                </p>
                <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-foreground mb-8">
                  {props.faqTitle ?? "Frequently Asked Questions"}
                </h2>
                <div className="luxury-card rounded-lg overflow-hidden">
                  {props.faqImage && (
                    <img
                      src={props.faqImage}
                      alt={props.faqImageAlt ?? ""}
                      width={900}
                      height={600}
                      loading="lazy"
                      className="w-full h-56 sm:h-64 object-cover"
                    />
                  )}
                  <div className="p-6 sm:p-8">
                    <h3 className="font-heading text-2xl font-semibold text-foreground mb-2">Still Have a Question?</h3>
                    <p className="text-muted-foreground leading-relaxed mb-6">
                      Text me. I reply personally, and I'm happy to check your date while we're at it.
                    </p>
                    <ContactButtons stacked />
                  </div>
                </div>
              </aside>
              <FaqList faqs={props.faqs} spacing="space-y-4" />
            </div>
          ) : (
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <p className="text-primary text-sm tracking-[0.3em] uppercase mb-4 font-medium">
                  Frequently Asked
                </p>
                <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-foreground">
                  {props.faqTitle ?? "Frequently Asked Questions"}
                </h2>
              </div>
              <FaqList faqs={props.faqs} spacing="space-y-6" />
            </div>
          )}
        </AnimatedSection>

        {/* Related */}
        <AnimatedSection className="section-padding bg-background">
          <div className={`${props.rich ? "max-w-7xl" : "max-w-5xl"} mx-auto px-4 sm:px-6 lg:px-8`}>
            <div className="text-center mb-12">
              <p className="text-primary text-sm tracking-[0.3em] uppercase mb-4 font-medium">
                Explore More
              </p>
              <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-foreground">
                {props.relatedTitle ?? "Related Services"}
              </h2>
            </div>
            <div className={`grid gap-6 ${props.rich && props.related.length === 4 ? "sm:grid-cols-2 lg:grid-cols-4" : "md:grid-cols-3"}`}>
              {props.related.map((r) => (
                <Link
                  key={r.to}
                  to={r.to}
                  className="luxury-card rounded-lg overflow-hidden hover:shadow-xl transition-all group flex flex-col"
                >
                  {r.image && r.imageWhole && (
                    <WholePhoto src={r.image} alt="" className={props.rich ? "h-56" : "h-44"} />
                  )}
                  {r.image && !r.imageWhole && (
                    <img
                      src={r.image}
                      alt=""
                      width={800}
                      height={500}
                      loading="lazy"
                      className={`w-full ${props.rich ? "h-56" : "h-44"} object-cover transition-transform duration-700 group-hover:scale-105`}
                    />
                  )}
                  <div className="p-6 flex-1 flex flex-col">
                    <h3 className="font-heading text-lg font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">
                      {r.title}
                    </h3>
                    <p className="text-muted-foreground text-sm mb-3 flex-1">{r.desc}</p>
                    <span className="inline-flex items-center gap-1 text-primary text-sm font-medium">
                      Learn more <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </AnimatedSection>


        {/* Text Maria (no form): every Check My Date button scrolls here */}
        <TextMariaSection title={props.ctaTitle} body={props.ctaBody} />
        {props.showCorporateForm && <CorporateAccountForm compact />}
      </main>
      <Footer />
      <FloatingButtons />
    </>
  );
}

export function buildLocalBusinessSchema({
  url,
  name,
  image,
  description,
  serviceType,
  areaServed,
}: {
  url: string;
  name: string;
  image: string;
  description: string;
  serviceType: string;
  areaServed: { type: "City" | "Airport"; name: string }[];
}) {
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": url,
    name,
    alternateName: "Empress Elite Limousine",
    image,
    url,
    telephone: "+1-678-431-7222",
    priceRange: "$$$",
    address: {
      "@type": "PostalAddress",
      streetAddress: "3401 Norman Berry Dr Suite 146",
      addressLocality: "Atlanta",
      addressRegion: "GA",
      postalCode: "30344",
      addressCountry: "US",
    },
    areaServed: areaServed.map((a) => ({ "@type": a.type, name: a.name })),
    serviceType,
    description,
  };
}

export function buildFaqSchema(faqs: FaqItem[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}
